/**
 * Phase 19.1 — RGM Control Center
 *
 * Founder/admin-only diagnostic + control dashboard at `/admin/control-center`.
 *
 * Surface map (matches backend /api/admin/control-center):
 *   1. Platform Health  — colored service cards + Recheck
 *   2. Error Monitor    — recent failed endpoints
 *   3. Quick Fix Tools  — confirm-required maintenance actions
 *   4. Community Control
 *   5. Reports Control
 *   6. Beat Department Control
 *   7. Payout / Economy
 *   8. Growth Dashboard
 *
 * Every write action requires a confirm dialog. Reads are passive.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";
import HomepageControlsSection from "@/components/admin/HomepageControlsSection";
import AdminCoinGrantsSection from "@/components/AdminCoinGrantsSection";
import ContestFlagToggle from "@/components/contests/ContestFlagToggle";
import ContestJudgingFlagToggle from "@/components/contests/ContestJudgingFlagToggle";
import AdminContestBackfillSection from "@/components/admin/AdminContestBackfillSection";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  green: { dot: "bg-emerald-500", border: "border-emerald-500/30", text: "text-emerald-300", label: "Healthy" },
  yellow: { dot: "bg-amber-400", border: "border-amber-500/40", text: "text-amber-200", label: "Degraded" },
  red: { dot: "bg-rose-500", border: "border-rose-500/50", text: "text-rose-300", label: "Failing" },
};

const Section = ({ id, title, children, action }) => (
  <section id={id} className="mb-10" data-testid={`cc-section-${id}`}>
    <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
      <h2 className="text-xl sm:text-2xl font-black text-white">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Tile = ({ label, value, sub, testId }) => (
  <div
    className="rounded-lg bg-white/[0.03] border border-white/10 p-4"
    data-testid={testId}
  >
    <p className="text-[11px] uppercase tracking-widest text-gray-400">{label}</p>
    <p className="text-2xl font-black text-white mt-1">{value ?? "—"}</p>
    {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
  </div>
);

const ServiceCard = ({ s }) => {
  const style = STATUS_STYLE[s.status] || STATUS_STYLE.yellow;
  return (
    <div
      className={`rounded-lg bg-white/[0.03] border ${style.border} p-4`}
      data-testid={`cc-service-${s.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-white text-sm font-bold truncate">{s.label || s.id}</p>
        <span className={`inline-flex items-center gap-1.5 text-[11px] ${style.text}`}>
          <span className={`w-2 h-2 rounded-full ${style.dot}`} />
          {style.label}
        </span>
      </div>
      {s.message && <p className="text-amber-200 text-xs mt-2">{s.message}</p>}
      {s.error && <p className="text-rose-200 text-xs mt-2 break-words">{s.error}</p>}
      {s.stats && (
        <p className="text-gray-500 text-[11px] mt-2 break-words">
          {Object.entries(s.stats).map(([k, v]) => `${k}: ${v}`).join(" · ")}
        </p>
      )}
      <p className="text-[10px] text-gray-600 mt-2">
        {s.last_checked ? `Checked ${new Date(s.last_checked).toLocaleString()}` : ""}
        {typeof s.latency_ms === "number" ? ` · ${s.latency_ms}ms` : ""}
      </p>
    </div>
  );
};

const ConfirmDialog = ({ open, title, body, onCancel, onConfirm, confirmLabel = "Run" }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
      data-testid="cc-confirm-dialog"
    >
      <div
        className="w-full max-w-md bg-[#13131a] border border-purple-500/40 rounded-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-black text-white">{title}</h3>
        <p className="text-sm text-gray-300 mt-2">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} className="text-white border-gray-700"
                  data-testid="cc-confirm-cancel">Cancel</Button>
          <Button onClick={onConfirm} className="bg-rose-600 hover:bg-rose-500 text-white"
                  data-testid="cc-confirm-yes">{confirmLabel}</Button>
        </div>
      </div>
    </div>
  );
};

const DiscoveryToggleCard = ({ id, title, description, on, onClick, disabled, onLabel = "ON", offLabel = "OFF", tone = "emerald" }) => {
  const toneOn = tone === "emerald" ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10";
  const pillOn = tone === "emerald" ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-100" : "bg-amber-500/20 border-amber-400/60 text-amber-100";
  return (
    <div
      className={`rounded-xl border p-4 ${on ? toneOn : "border-white/10 bg-white/[0.04]"}`}
      data-testid={`cc-toggle-card-${id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-white font-black text-sm">{title}</p>
          <p className="text-[11px] text-gray-400 mt-1">{description}</p>
        </div>
        <span
          className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${on ? pillOn : "bg-white/5 border-white/20 text-gray-300"}`}
          data-testid={`cc-toggle-state-${id}`}
        >
          {on ? onLabel : offLabel}
        </span>
      </div>
      <Button
        onClick={onClick}
        disabled={disabled}
        className={`mt-3 w-full ${on ? "bg-rose-500 hover:bg-rose-400 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-black font-black"}`}
        data-testid={`cc-toggle-btn-${id}`}
      >
        {disabled ? "Saving…" : on ? `Turn OFF` : `Turn ON`}
      </Button>
    </div>
  );
};

const DiscoveryAndBeatToggles = () => {
  // iter_205 — Two admin-only settings groups:
  //  • Community Pulse public visibility (default OFF)
  //  • Beat automation: auto-feature + BOTW auto-pick (both default ON)
  const [pulseVisible, setPulseVisible] = useState(null);
  const [beatFlags, setBeatFlags] = useState(null);
  const [busy, setBusy] = useState({});

  const refresh = useCallback(async () => {
    try {
      const [pv, bf] = await Promise.all([
        api.get("/admin/community-pulse/visibility"),
        api.get("/admin/beats/automation"),
      ]);
      setPulseVisible(!!pv.data?.visible);
      setBeatFlags(bf.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load automation toggles");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const togglePulse = async () => {
    const next = !pulseVisible;
    setBusy((b) => ({ ...b, pulse: true }));
    try {
      const r = await api.put("/admin/community-pulse/visibility", { visible: next });
      setPulseVisible(!!r.data?.visible);
      toast.success(r.data?.visible
        ? "🟢 Community Pulse widget is now visible on discovery"
        : "⏸ Community Pulse widget is hidden from users");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy((b) => ({ ...b, pulse: false }));
    }
  };

  const toggleBeatFlag = async (key) => {
    const next = !(beatFlags?.[key]);
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await api.put("/admin/beats/automation", { [key]: next });
      setBeatFlags(r.data || {});
      toast.success(`${key.replace(/_/g, " ")}: ${r.data?.[key] ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };


  return (
    <Section id="discovery-widgets" title="3b. Discovery Widgets + Beat Automation">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <DiscoveryToggleCard
          id="community-pulse"
          title="💓 Community Pulse (public widget)"
          description="Show the live activity card on Dashboard, Discovery, and Creator Hub. Presence tracking keeps running underneath — this only controls the public card. Default OFF."
          on={!!pulseVisible}
          onClick={togglePulse}
          disabled={!!busy.pulse || pulseVisible === null}
        />
        <DiscoveryToggleCard
          id="beat-auto-feature"
          title="⭐ Auto-Feature Beats"
          description="When a beat becomes Beat of the Week, automatically flip beats.featured=true on the marketplace. Turn OFF to require manual featuring only."
          on={!!beatFlags?.auto_feature_enabled}
          onClick={() => toggleBeatFlag("auto_feature_enabled")}
          disabled={!!busy.auto_feature_enabled || !beatFlags}
        />
        <DiscoveryToggleCard
          id="botw-auto-pick"
          title="🎖️ Beat of the Week auto-pick"
          description="When no admin pin exists for the current week, auto-pick the top-played beat. Turn OFF if you want BOTW to be admin-pinned only (recommended for contest-driven flows)."
          on={!!beatFlags?.botw_auto_pick_enabled}
          onClick={() => toggleBeatFlag("botw_auto_pick_enabled")}
          disabled={!!busy.botw_auto_pick_enabled || !beatFlags}
        />
      </div>
    </Section>
  );
};

// 4a. RGM Official Account (bootstrap) — one-time initial activation
// target user_id. Kept as a module constant so tests can grep for it
// and admins can visually confirm the target in the candidate table.
// NOT a runtime identity signal — the runtime resolver still trusts
// only ``users.is_official == true`` on the backend.
const RGM_BOOTSTRAP_UID = "user_1b79cf7ec584";

const AdminControlCenterPage = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [errorsData, setErrorsData] = useState(null);
  const [community, setCommunity] = useState(null);
  const [reportsOverview, setReportsOverview] = useState(null);
  const [beats, setBeats] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [liveHealth, setLiveHealth] = useState(null);    // /admin/live/health
  const [lkHealth, setLkHealth] = useState(null);        // /live/livekit/health
  const [liveHealthRefreshing, setLiveHealthRefreshing] = useState(false);
  const [liveHealthLastRefreshedAt, setLiveHealthLastRefreshedAt] = useState(null);
  const [liveHealthRefreshError, setLiveHealthRefreshError] = useState(null);
  const [reapingAll, setReapingAll] = useState(false);
  const [cleaningStaleLk, setCleaningStaleLk] = useState(false);
  // Phase 21.3a2 — Stripe Connect diagnostics panel state
  const [stripeDiag, setStripeDiag] = useState(null);
  const [stripeDiagLoading, setStripeDiagLoading] = useState(false);
  const [stripeDiagError, setStripeDiagError] = useState(null);
  const [stripeDiagFetchedAt, setStripeDiagFetchedAt] = useState(null);
  // Phase 21.3a3 — Live test-onboard capture panel state
  const [stripeTestRunning, setStripeTestRunning] = useState(false);
  const [stripeTestResult, setStripeTestResult] = useState(null);
  const [stripeTestFetchedAt, setStripeTestFetchedAt] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null); // { action, label, body }

  // 4a. RGM Official Account (bootstrap) — READ-ONLY candidate probe +
  // confirm-gated activation on the already-deployed backend endpoints
  //   GET  /api/admin/official-account/candidates
  //   POST /api/admin/official-account/activate
  const [officialCandidates, setOfficialCandidates] = useState(null);
  const [officialCurrentUid, setOfficialCurrentUid] = useState(null);
  const [officialLoading, setOfficialLoading] = useState(false);
  const [officialActivating, setOfficialActivating] = useState(null); // user_id in-flight
  const [officialError, setOfficialError] = useState(null);
  const [officialFetchedAt, setOfficialFetchedAt] = useState(null);

  // --- Boot: identity check ---
  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        const u = r.data;
        const isFounder = (u?.badges || []).includes("founder");
        const isAdmin = u?.role === "admin" || (u?.badges || []).includes("admin");
        if (!isFounder && !isAdmin) {
          setBootError("Founder/admin access only.");
          return;
        }
        setMe(u);
      })
      .catch(() => {
        setBootError("You must be signed in as an admin.");
      });
  }, []);

  // --- Loaders ---
  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const r = await api.get("/admin/control-center/health");
      setHealth(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load health");
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const loadErrors = useCallback(async () => {
    try {
      const r = await api.get("/admin/control-center/errors");
      setErrorsData(r.data);
    } catch (e) {
      // Errors collection may not exist yet — soft fail
      setErrorsData({ by_endpoint: [], recent: [], total: 0 });
    }
  }, []);

  const loadCommunity = useCallback(async () => {
    try { setCommunity((await api.get("/admin/control-center/community")).data); } catch (_e) { /* soft fail */ }
  }, []);
  const loadReports = useCallback(async () => {
    try { setReportsOverview((await api.get("/admin/control-center/reports")).data); } catch (_e) { /* soft fail */ }
  }, []);
  const loadBeats = useCallback(async () => {
    try { setBeats((await api.get("/admin/control-center/beats")).data); } catch (_e) { /* soft fail */ }
  }, []);
  const loadPayouts = useCallback(async () => {
    try { setPayouts((await api.get("/admin/control-center/payouts")).data); } catch (_e) { /* soft fail */ }
  }, []);
  const loadGrowth = useCallback(async () => {
    try { setGrowth((await api.get("/admin/control-center/growth")).data); } catch (_e) { /* soft fail */ }
  }, []);
  const loadLiveHealth = useCallback(async () => {
    setLiveHealthRefreshing(true);
    setLiveHealthRefreshError(null);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/live/health"),
        api.get("/live/livekit/health"),
      ]);
      setLiveHealth(a.data);
      setLkHealth(b.data);
      setLiveHealthLastRefreshedAt(new Date());
    } catch (e) {
      setLiveHealthRefreshError(
        e?.response?.data?.detail || e?.message || "Refresh failed"
      );
    } finally {
      setLiveHealthRefreshing(false);
    }
  }, []);

  // Phase 21.3a2 — Stripe Connect diagnostics loader. Uses the existing
  // authenticated axios client so no manual Bearer token is required —
  // the admin's session cookie / token is applied automatically.
  const loadStripeDiag = useCallback(async () => {
    setStripeDiagLoading(true);
    setStripeDiagError(null);
    try {
      const r = await api.get("/stripe/connect/diagnostics");
      setStripeDiag(r.data);
      setStripeDiagFetchedAt(new Date());
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : (d && d.message) || e?.message || "Failed to load diagnostics";
      setStripeDiagError(msg);
    } finally {
      setStripeDiagLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!me) return;
    loadHealth();
    loadErrors();
    loadCommunity();
    loadReports();
    loadBeats();
    loadPayouts();
    loadGrowth();
    loadLiveHealth();
    loadStripeDiag();
  }, [me, loadHealth, loadErrors, loadCommunity, loadReports, loadBeats, loadPayouts, loadGrowth, loadLiveHealth, loadStripeDiag]);

  // Phase 21.3a3 — Run a full test onboarding round-trip against the
  // live Stripe key and capture the COMPLETE raw response — success or
  // error — for support tickets. Ephemeral (cleans up its own account).
  const runStripeTestOnboard = useCallback(async () => {
    if (!window.confirm(
      "Run a REAL Stripe onboarding attempt against the currently loaded key?\n\n" +
      "This creates an Express Connect account, generates an onboarding " +
      "link, then IMMEDIATELY deletes the ephemeral account. On failure " +
      "you'll see the COMPLETE raw Stripe response including request_id."
    )) return;
    setStripeTestRunning(true);
    setStripeTestResult(null);
    try {
      const r = await api.post("/stripe/connect/diagnostics/test-onboard");
      setStripeTestResult({ ok: true, http_status: 200, data: r.data });
      setStripeTestFetchedAt(new Date());
    } catch (e) {
      // We WANT the raw error body — do not summarise it here.
      setStripeTestResult({
        ok: false,
        http_status: e?.response?.status,
        data: e?.response?.data,
        raw_message: e?.message,
      });
      setStripeTestFetchedAt(new Date());
    } finally {
      setStripeTestRunning(false);
    }
  }, []);

  // --- Quick-fix runner with confirm gate ---
  const runAction = async (path, label, payload = {}, requireConfirm = true) => {
    if (requireConfirm) {
      setConfirmFor({
        action: async () => {
          setConfirmFor(null);
          await actuallyRun(path, label, payload);
        },
        label,
        body: `This will run "${label}". The action is audited. Continue?`,
      });
      return;
    }
    await actuallyRun(path, label, payload);
  };

  const actuallyRun = async (path, label, payload) => {
    setBusyAction(label);
    try {
      const body = { confirm: true, ...payload };
      const r = await api.post(path, body);
      toast.success(`${label}: ${r.data?.ok ? "ok" : "done"}`);
      // After any write, refresh health
      await loadHealth();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : `${label} failed`);
    } finally {
      setBusyAction(null);
    }
  };

  // 4a. RGM Official Account — READ ONLY candidate loader. Uses the
  // shared authenticated api client so it rides the admin browser
  // session already in localStorage. Never mutates state on 401/403.
  const loadOfficialCandidates = useCallback(async () => {
    setOfficialLoading(true);
    setOfficialError(null);
    try {
      const r = await api.get("/admin/official-account/candidates");
      setOfficialCandidates(Array.isArray(r.data?.candidates) ? r.data.candidates : []);
      setOfficialCurrentUid(r.data?.current_official_user_id || null);
      setOfficialFetchedAt(new Date());
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg =
        status === 401 || status === 403
          ? "Admin authentication required to view official-account candidates."
          : (typeof detail === "string" ? detail : "Could not load official-account candidates.");
      setOfficialError(msg);
      toast.error(msg);
    } finally {
      setOfficialLoading(false);
    }
  }, []);

  // 4a. RGM Official Account — activation is confirm-gated. NEVER
  // auto-retries the POST. Distinguishes 400 (confirm missing),
  // 401/403 (auth), 404 (missing target user), 409 (another account
  // is already is_official). On success, re-loads the candidate
  // table so the operator sees is_official: true reflected.
  const activateOfficial = (uid, artistName) => {
    if (!uid) return;
    setConfirmFor({
      label: "Activate RGM Official Account",
      body: (
        `Set is_official = true on user_id "${uid}"` +
        (artistName ? ` (${artistName})` : "") +
        `. This is a one-time bootstrap. The POST body sent is exactly ` +
        `{ user_id: "${uid}", confirm: true } — no other fields. Continue?`
      ),
      action: async () => {
        setConfirmFor(null);
        setOfficialActivating(uid);
        try {
          const r = await api.post("/admin/official-account/activate", {
            user_id: uid,
            confirm: true,
          });
          const activated = r.data?.activated;
          const already = r.data?.already_official;
          toast.success(
            already
              ? `Already official: ${uid}`
              : activated
                ? `Activated official account: ${uid}`
                : `Activate call returned no-op for ${uid}`
          );
          await loadOfficialCandidates();
        } catch (e) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail;
          let msg;
          if (status === 401 || status === 403) {
            msg = typeof detail === "string" ? detail : "Not authorized to activate.";
          } else if (status === 404) {
            msg = "Target user_id not found.";
          } else if (status === 409) {
            msg = typeof detail === "string" ? detail : "Another account is already official. R1 does not support switching.";
          } else if (status === 400) {
            msg = typeof detail === "string" ? detail : "Bad request (confirm required).";
          } else {
            msg = typeof detail === "string" ? detail : "Activation failed.";
          }
          toast.error(msg);
          // Do NOT auto-retry the write. The operator must re-click.
        } finally {
          setOfficialActivating(null);
        }
      },
    });
  };

  // --- Live Health quick actions (wire existing endpoints, no new backend) ---
  const handleReapAllGhosts = async () => {
    const rooms = (liveHealth?.rooms || []).filter(r => (r.ghost_count || 0) > 0);
    if (rooms.length === 0) {
      toast.success("No ghost rooms to reap 🎉");
      return;
    }
    setReapingAll(true);
    let reaped = 0;
    let failed = 0;
    for (const r of rooms) {
      try {
        const resp = await api.post(`/live/rooms/${r.room_id}/admin/reap-ghosts`);
        reaped += Number(resp.data?.reaped || 0);
      } catch (_e) {
        failed += 1;
      }
    }
    toast.success(`Reaped ${reaped} ghost(s) across ${rooms.length} room(s)${failed ? ` (${failed} failed)` : ""}`);
    await loadLiveHealth();
    setReapingAll(false);
  };

  const handleCleanupStaleLk = async () => {
    setCleaningStaleLk(true);
    try {
      const r = await api.post("/live/livekit/cleanup-stale-rooms");
      toast.success(`Deleted ${r.data?.deleted_count ?? 0} stale LiveKit room(s)`);
      await loadLiveHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaningStaleLk(false);
    }
  };

  const overallChip = useMemo(() => {
    if (!health) return null;
    const s = STATUS_STYLE[health.overall] || STATUS_STYLE.yellow;
    return (
      <span
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border ${s.border} ${s.text} text-xs font-bold`}
        data-testid="cc-overall-chip"
      >
        <span className={`w-2 h-2 rounded-full ${s.dot}`} />
        {s.label} · {health.counts?.green || 0} green · {health.counts?.yellow || 0} yellow · {health.counts?.red || 0} red
      </span>
    );
  }, [health]);

  if (bootError) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center p-6" data-testid="cc-blocked">
        <Card className="max-w-md p-6 bg-white/5 border-rose-500/30">
          <h1 className="text-xl font-black text-white">🛑 Control Center</h1>
          <p className="text-rose-300 mt-2">{bootError}</p>
          <Button onClick={() => navigate("/")} className="mt-4 bg-purple-600 text-white">Home</Button>
        </Card>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center" data-testid="cc-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="admin-control-center">
      <SharedNavigation currentUser={me} activePage="admin" />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3">
              🛰️ <span>RGM Control Center</span>
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Founder dashboard for platform health, diagnostics, and quick fixes.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {overallChip}
            <Button
              onClick={() => { loadHealth(); loadErrors(); loadCommunity(); loadReports(); loadBeats(); loadPayouts(); loadGrowth(); loadLiveHealth(); loadStripeDiag(); }}
              variant="outline"
              className="text-white border-gray-700"
              data-testid="cc-refresh-all"
              disabled={healthLoading}
            >
              {healthLoading ? "Refreshing…" : "🔄 Refresh all"}
            </Button>
          </div>
        </div>

        {/* Anchor nav */}
        <div className="flex gap-2 flex-wrap text-xs mb-8">
          {[
            ["health", "1. Health"],
            ["live-health", "📡 Live Health"],
            ["stripe-diag", "💳 Stripe Diag"],
            ["errors", "2. Error Monitor"],
            ["quickfix", "3. Quick Fix"],
            ["community", "4. Community"],
            ["reports", "5. Reports"],
            ["beats", "6. Beat Dept"],
            ["payouts", "7. Payouts"],
            ["growth", "8. Growth"],
            ["homepage-controls", "🏠 Homepage"],
            ["contest-center", "🏆 Contests"],
          ].map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-gray-200 hover:bg-purple-500/10 hover:border-purple-400/40"
              data-testid={`cc-nav-${id}`}
            >
              {label}
            </a>
          ))}
          {/* Phase 22 iter_174 — Manage Memberships jump-link (opens dedicated page) */}
          <Link
            to="/admin/memberships"
            className="px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-400/50 text-purple-100 hover:bg-purple-500/20 font-black"
            data-testid="cc-nav-memberships"
          >
            💎 Manage Memberships →
          </Link>
          {/* iter_176 — Visible Live Diagnostics jump-link */}
          <a
            href="#live-health"
            className="px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-400/50 text-rose-100 hover:bg-rose-500/20 font-black"
            data-testid="cc-nav-live-diagnostics"
          >
            🔧 Live Diagnostics
          </a>
          {/* Coin Fund Reserve — Phase A3 admin monitoring dashboard */}
          <Link
            to="/admin/coin-reserve"
            className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/50 text-emerald-100 hover:bg-emerald-500/20 font-black"
            data-testid="cc-nav-coin-reserve"
          >
            🧾 Coin Fund Reserve
          </Link>
          {/* Coin Rush — Phase 2 Admin Control Center (promotional,
              financially inert leaderboard — does not touch gifts,
              wallets, earnings, or payouts). */}
          <Link
            to="/admin/coin-rush"
            className="px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/50 text-amber-100 hover:bg-amber-500/20 font-black"
            data-testid="cc-nav-coin-rush"
          >
            🪙 Coin Rush
          </Link>
          {/* Feb 2026 — Share Card Control visibility gate the CEO
              could not previously discover through Admin navigation. */}
          <Link
            to="/admin/creative/share-cards"
            className="px-3 py-1.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-400/50 text-fuchsia-100 hover:bg-fuchsia-500/20 font-black"
            data-testid="cc-nav-share-card-control"
          >
            🎴 Share Card Control
          </Link>
          {/* Feb 2026 — Menu Organizer (Phase 1A). Editor for the
              existing navigation system, not a new admin surface. */}
          <Link
            to="/admin/creative/menu-organizer"
            className="px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-400/50 text-indigo-100 hover:bg-indigo-500/20 font-black"
            data-testid="cc-nav-menu-organizer"
          >
            🧭 Menu Organizer
          </Link>
        </div>

        {/* 1. Platform Health */}
        <Section
          id="health"
          title="1. Platform Health"
          action={
            <div className="flex flex-wrap gap-2 items-center">
              <Link to="/admin/community">
                <Button
                  variant="outline"
                  className="text-white border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/20"
                  data-testid="cc-open-community-mod"
                >
                  🛡️ Community Control
                </Button>
              </Link>
              <Button
                onClick={() => runAction("/admin/control-center/actions/recheck-all", "Recheck all services", {}, false)}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="cc-recheck-all"
                disabled={busyAction === "Recheck all services"}
              >
                {busyAction === "Recheck all services" ? "Rechecking…" : "🔁 Recheck all"}
              </Button>
            </div>
          }
        >
          {!health ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {health.services.map((s) => <ServiceCard key={s.id} s={s} />)}
            </div>
          )}
        </Section>

        {/* 1b. Live Health (wired to existing /admin/live/health + /live/livekit/health) */}
        <Section
          id="live-health"
          title="📡 Live Health (LiveKit + Active Rooms)"
          action={
            <div className="flex gap-2 flex-wrap items-center">
              <span
                className="text-[11px] text-gray-400"
                data-testid="cc-live-health-last-refreshed"
              >
                {liveHealthRefreshing
                  ? "Refreshing…"
                  : liveHealthLastRefreshedAt
                  ? `Updated ${liveHealthLastRefreshedAt.toLocaleTimeString()}`
                  : "Not yet refreshed"}
                {liveHealthRefreshError && (
                  <span
                    className="ml-2 text-rose-300"
                    data-testid="cc-live-health-refresh-error"
                  >
                    · ⚠ {liveHealthRefreshError}
                  </span>
                )}
              </span>
              <Link to="/admin/live-health">
                <Button
                  variant="outline"
                  className="text-white border-gray-700"
                  data-testid="cc-open-live-health"
                >
                  Open full dashboard →
                </Button>
              </Link>
              <Button
                onClick={loadLiveHealth}
                variant="outline"
                disabled={liveHealthRefreshing}
                className="text-white border-gray-700"
                data-testid="cc-live-health-refresh"
              >
                {liveHealthRefreshing ? "↻ Refreshing…" : "↻ Refresh"}
              </Button>
              {/* iter_176 — Private Test Mode: create an admin-only room that
                  skips public listing + follower fanout notifications. */}
              <Button
                onClick={async () => {
                  try {
                    const r = await api.post("/live/rooms", {
                      title: `Admin test — ${new Date().toLocaleTimeString()}`,
                      topic: "LiveKit diagnostic — not public",
                      allow_guests: false,
                      video_enabled: false,
                      is_test_mode: true,
                    });
                    const rid = r.data?.room?.room_id;
                    if (rid) {
                      toast.success(`🧪 Private test room started — opening…`);
                      // iter_176 fix — SPA nav to LiveRoomPage (registered
                      // at /beams/:roomId in App.js). The room is hidden
                      // from the public list because is_test_mode=true.
                      navigate(`/beams/${rid}`);
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not start test room");
                  }
                }}
                className="bg-rose-500/80 hover:bg-rose-500 text-white"
                data-testid="cc-live-test-mode-btn"
                title="Create a private admin-only room. Not publicly listed. Doesn't notify followers."
              >
                🧪 Start Private Test Room
              </Button>
            </div>
          }
        >
          {!liveHealth ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Tile
                  label="LiveKit status"
                  value={lkHealth?.status ? lkHealth.status.replace("_", " ") : "—"}
                  sub={lkHealth?.ws_url || ""}
                  testId="cc-live-lk-status"
                />
                <Tile
                  label="Active rooms"
                  value={liveHealth.active_room_count ?? 0}
                  testId="cc-live-active-rooms"
                />
                <Tile
                  label="Total viewers (WS)"
                  value={liveHealth.total_ws_connections ?? 0}
                  testId="cc-live-total-viewers"
                />
                <Tile
                  label="Ghost rooms"
                  value={(liveHealth.rooms || []).filter(r => (r.ghost_count || 0) > 0).length}
                  sub={`${(liveHealth.rooms || []).reduce((a, r) => a + (r.ghost_count || 0), 0)} total ghosts`}
                  testId="cc-live-ghost-rooms"
                />
              </div>

              {lkHealth?.recommendation && (
                <p
                  className="mb-3 text-xs text-amber-100 bg-amber-500/10 border border-amber-500/30 rounded-md p-2"
                  data-testid="cc-live-lk-recommendation"
                >
                  💡 {lkHealth.recommendation}
                </p>
              )}

              <div className="flex gap-2 flex-wrap mb-4">
                <Button
                  onClick={handleReapAllGhosts}
                  disabled={reapingAll}
                  className="bg-rose-600 hover:bg-rose-500 text-white"
                  data-testid="cc-live-reap-ghosts-btn"
                >
                  {reapingAll ? "Reaping…" : "👻 Reap all ghost rooms"}
                </Button>
                <Button
                  onClick={handleCleanupStaleLk}
                  disabled={cleaningStaleLk}
                  variant="outline"
                  className="text-white border-rose-500/40"
                  data-testid="cc-live-cleanup-stale-btn"
                >
                  {cleaningStaleLk ? "Cleaning…" : "🧹 Cleanup stale LiveKit rooms"}
                </Button>
              </div>

              {(liveHealth.rooms || []).length > 0 ? (
                <Card className="p-3 bg-white/[0.03] border-white/10">
                  <p className="text-white text-sm font-bold mb-2">Top active rooms</p>
                  <div className="space-y-1.5">
                    {liveHealth.rooms.slice(0, 5).map((rm) => (
                      <div
                        key={rm.room_id}
                        className="flex items-center justify-between gap-2 text-xs"
                        data-testid={`cc-live-room-${rm.room_id}`}
                      >
                        <Link
                          to={`/beams/${rm.room_id}`}
                          className="text-pink-300 hover:underline truncate"
                        >
                          {rm.title || "Untitled"}
                          <span className="text-gray-500"> · {rm.host_name || rm.host_user_id}</span>
                        </Link>
                        <span className="text-gray-300 flex-shrink-0">
                          {rm.ws_online_count}👥 · {rm.speaker_count}🎤
                          {rm.ghost_count > 0 && (
                            <span className="text-amber-300 ml-1">+{rm.ghost_count}👻</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              ) : (
                <p className="text-sm text-gray-500" data-testid="cc-live-empty">
                  No active rooms right now.
                </p>
              )}
            </>
          )}
        </Section>

        {/* iter_217 — Notification Diagnostics (route existed since
            iter_212 but had no visible menu link). Standalone Section
            so admins can find it on the Control Center without
            expanding a nested action slot. */}
        <Section
          id="notification-diagnostics"
          title="🔔 Notification Diagnostics"
          action={
            <Link to="/admin/notifications-health">
              <Button
                variant="outline"
                className="text-white border-purple-500/40 hover:bg-purple-500/10 font-black"
                data-testid="cc-open-notifications-health"
              >
                Open full dashboard →
              </Button>
            </Link>
          }
        >
          <p className="text-sm text-gray-300 max-w-3xl">
            Read-only view of the last 24h of notifications: category,
            recipient, age, in-app read state, and push delivery status.
            Use this to diagnose late, missing, or misrouted messages.
            Device tokens and provider secrets are never displayed.
          </p>
        </Section>

        {/* Phase 21.3a2 — Stripe Connect Diagnostics (admin-only) */}
        <Section
          id="stripe-diag"
          title="💳 Stripe Connect Diagnostics"
          action={
            <div className="flex gap-2 items-center flex-wrap">
              <span
                className="text-[11px] text-gray-400"
                data-testid="cc-stripe-diag-fetched-at"
              >
                {stripeDiagLoading
                  ? "Loading…"
                  : stripeDiagFetchedAt
                  ? `Fetched ${stripeDiagFetchedAt.toLocaleTimeString()}`
                  : "Not yet fetched"}
              </span>
              <Button
                onClick={loadStripeDiag}
                variant="outline"
                disabled={stripeDiagLoading}
                className="text-white border-gray-700"
                data-testid="cc-stripe-diag-refresh"
              >
                {stripeDiagLoading ? "↻ Loading…" : "↻ Refresh"}
              </Button>
            </div>
          }
        >
          {stripeDiagError ? (
            <Card
              className="p-3 bg-rose-500/10 border-rose-500/30 text-sm text-rose-200"
              data-testid="cc-stripe-diag-error"
            >
              ⚠ {stripeDiagError}
            </Card>
          ) : !stripeDiag ? (
            <p className="text-sm text-gray-500">
              {stripeDiagLoading ? "Loading Stripe diagnostics…" : "No data yet."}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Tile
                  label="key_mode"
                  value={stripeDiag.key_mode || "—"}
                  sub={stripeDiag.key_last4 ? `…${stripeDiag.key_last4}` : ""}
                  testId="cc-stripe-key-mode"
                />
                <Tile
                  label="publishable_key_mode"
                  value={stripeDiag.publishable_key_mode || "—"}
                  sub={
                    stripeDiag.publishable_key_last4
                      ? `…${stripeDiag.publishable_key_last4}`
                      : ""
                  }
                  testId="cc-stripe-pk-mode"
                />
                <Tile
                  label="webhook_configured"
                  value={stripeDiag.webhook_configured ? "yes" : "no"}
                  testId="cc-stripe-webhook-configured"
                />
                <Tile
                  label="platform_account.id"
                  value={stripeDiag.platform_account?.id || "—"}
                  testId="cc-stripe-platform-id"
                />
                <Tile
                  label="details_submitted"
                  value={
                    stripeDiag.platform_account?.details_submitted ? "true" : "false"
                  }
                  testId="cc-stripe-details-submitted"
                />
                <Tile
                  label="charges_enabled"
                  value={
                    stripeDiag.platform_account?.charges_enabled ? "true" : "false"
                  }
                  testId="cc-stripe-charges-enabled"
                />
                <Tile
                  label="payouts_enabled"
                  value={
                    stripeDiag.platform_account?.payouts_enabled ? "true" : "false"
                  }
                  testId="cc-stripe-payouts-enabled"
                />
                <Tile
                  label="business_type"
                  value={stripeDiag.platform_account?.business_type || "—"}
                  testId="cc-stripe-business-type"
                />
              </div>

              {stripeDiag.platform_error && (
                <Card
                  className="p-3 bg-amber-500/10 border-amber-500/30 text-xs text-amber-100 mb-3"
                  data-testid="cc-stripe-platform-error"
                >
                  <p className="font-bold text-amber-200 mb-1">Platform probe error</p>
                  <p className="mb-1">{stripeDiag.platform_error.message}</p>
                  {stripeDiag.platform_error.request_id && (
                    <p className="text-[10px] text-amber-200/70">
                      request_id: {stripeDiag.platform_error.request_id}
                    </p>
                  )}
                </Card>
              )}

              {/* Phase 21.3a3 — Live test-onboard round-trip capture */}
              <Card
                className="p-3 bg-white/[0.03] border-white/10 mb-3"
                data-testid="cc-stripe-test-onboard-card"
              >
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div>
                    <p className="font-bold text-white text-sm">
                      🧪 Test Onboarding (raw Stripe capture)
                    </p>
                    <p className="text-[11px] text-gray-400">
                      Creates + immediately deletes an ephemeral Express
                      account. Shows the COMPLETE raw Stripe response —
                      success or error — including <code>request_id</code>.
                    </p>
                  </div>
                  <Button
                    onClick={runStripeTestOnboard}
                    disabled={stripeTestRunning}
                    className="bg-rose-600 hover:bg-rose-500 text-white"
                    data-testid="cc-stripe-test-onboard-btn"
                  >
                    {stripeTestRunning ? "Running…" : "Run test onboarding"}
                  </Button>
                </div>
                {stripeTestResult && (
                  <div className="mt-2">
                    {(() => {
                      // Phase 21.3a3 — derive success from the backend
                      // payload (data.ok), NOT from the HTTP-level ok.
                      // The endpoint returns 200 with data.ok=false when
                      // it CAPTURED a Stripe error verbatim — that's a
                      // successful diagnostic, but the round-trip itself
                      // FAILED and the summary must reflect that.
                      const backendOk = !!stripeTestResult?.data?.ok;
                      const phase = stripeTestResult?.data?.phase;
                      return (
                    <p
                      className="text-[11px] text-gray-400 mb-1"
                      data-testid="cc-stripe-test-onboard-summary"
                    >
                      HTTP {stripeTestResult.http_status ?? "—"} ·{" "}
                      {backendOk ? (
                        <span className="text-emerald-300 font-bold">
                          onboarding round-trip succeeded
                        </span>
                      ) : (
                        <span className="text-rose-300 font-bold">
                          onboarding failed{phase ? ` at phase=${phase}` : ""} — see raw response below
                        </span>
                      )}
                      {stripeTestFetchedAt && (
                        <span className="ml-2 text-gray-500">
                          · {stripeTestFetchedAt.toLocaleTimeString()}
                        </span>
                      )}
                    </p>
                      );
                    })()}
                    <pre
                      className="p-3 bg-black/60 border border-white/10 rounded-md text-[11px] text-emerald-200 whitespace-pre-wrap break-all overflow-auto max-h-96"
                      data-testid="cc-stripe-test-onboard-raw"
                    >
                      {JSON.stringify(
                        stripeTestResult.data ?? { message: stripeTestResult.raw_message },
                        null,
                        2
                      )}
                    </pre>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-[11px] px-2 py-0.5"
                        onClick={() => {
                          try {
                            navigator.clipboard.writeText(
                              JSON.stringify(stripeTestResult.data ?? {}, null, 2)
                            );
                            toast.success("Raw response copied to clipboard");
                          } catch (_e) {
                            toast.error("Copy failed");
                          }
                        }}
                        data-testid="cc-stripe-test-onboard-copy"
                      >
                        📋 Copy raw JSON
                      </Button>
                      {(() => {
                        const rid =
                          stripeTestResult.data?.stripe_error?.request_id ||
                          stripeTestResult.data?.account_link_error?.request_id;
                        return rid ? (
                          <span
                            className="text-[11px] text-amber-200 self-center"
                            data-testid="cc-stripe-test-onboard-request-id"
                          >
                            request_id: {rid}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                )}
              </Card>

              {/* Quick interpretation helper — same 3-branch guide as
                  in the previous iteration's finish summary. */}
              <Card
                className="p-3 bg-white/[0.03] border-white/10 text-xs text-gray-300"
                data-testid="cc-stripe-diag-guide"
              >
                <p className="font-bold text-white mb-1">
                  How to read this:
                </p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>
                    <b>key_mode: test</b> → backend didn&apos;t reload the live key.
                    Redeploy / restart the backend.
                  </li>
                  <li>
                    <b>key_mode: live</b> + <b>details_submitted: false</b> →
                    live Connect platform profile still has an unsaved required
                    field.
                  </li>
                  <li>
                    <b>key_mode: live</b> + <b>details_submitted: true</b> but
                    /onboard still fails → forward{" "}
                    <code className="text-amber-200">stripe_error.request_id</code>{" "}
                    from the failing onboarding response to Stripe support.
                  </li>
                </ul>
              </Card>
            </>
          )}
        </Section>

        {/* 2. Error Monitor */}
        <Section id="errors" title="2. Error Monitor (last 24h)">
          {!errorsData ? <p className="text-sm text-gray-500">Loading…</p> : (
            errorsData.total === 0 ? (
              <p className="text-sm text-gray-500" data-testid="cc-errors-empty">
                No tracked errors. Either the error log collection is empty or every endpoint is healthy.
              </p>
            ) : (
              <div className="space-y-2">
                {errorsData.by_endpoint.slice(0, 12).map((row) => (
                  <div
                    key={row.endpoint}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg bg-rose-500/5 border border-rose-500/20"
                    data-testid={`cc-error-${row.endpoint}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-rose-200 text-sm font-mono truncate">{row.endpoint}</p>
                      {row.message && <p className="text-xs text-gray-400 mt-0.5 break-words">{row.message}</p>}
                      <p className="text-[10px] text-gray-500 mt-0.5">last: {row.last_seen ? new Date(row.last_seen).toLocaleString() : "—"}</p>
                    </div>
                    <span className="text-rose-300 font-black text-lg flex-shrink-0">×{row.count}</span>
                  </div>
                ))}
              </div>
            )
          )}
        </Section>

        {/* 3. Quick Fix Tools */}
        <Section id="quickfix" title="3. Quick Fix Tools">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { path: "/admin/control-center/actions/refresh-discovery-cache", label: "Refresh Discovery Feed cache", emoji: "🔥" },
              { path: "/admin/control-center/actions/rebuild-weekly-winners", label: "Rebuild Weekly Winners data", emoji: "🏆" },
              { path: "/admin/control-center/actions/backfill-collab-photos", label: "Backfill collaborator photos", emoji: "📸" },
              { path: "/admin/control-center/actions/backfill-user-aliases", label: "Backfill user aliases", emoji: "🔎" },
              { path: "/admin/control-center/actions/test-community-invite", label: "Test community invite endpoint", emoji: "📨", noConfirm: true },
              { path: "/admin/control-center/actions/test-report-edit", label: "Test report edit endpoint", emoji: "📰", noConfirm: true },
              { path: "/admin/control-center/actions/test-beat-marketplace", label: "Test beat marketplace endpoint", emoji: "🎹", noConfirm: true },
              { path: "/admin/control-center/actions/test-payout-audit", label: "Test payout audit", emoji: "💰", noConfirm: true },
              { path: "/admin/control-center/actions/test-cloudinary", label: "Test Cloudinary upload", emoji: "☁️", noConfirm: true },
              { path: "/admin/control-center/actions/test-ai-feedback", label: "Test AI feedback config", emoji: "🧠", noConfirm: true },
              { path: "/admin/control-center/actions/rebuild-beat-lanes", label: "Rebuild Beat lanes", emoji: "🎚️" },
              // Phase 20.2 — Promo Plays Audit (read-only dry run is safe)
              { path: "/admin/control-center/actions/audit-promo-plays", label: "🔍 Audit promo plays (dry-run)", emoji: "📊", noConfirm: true },
              { path: "/admin/control-center/actions/audit-promo-plays-run", label: "⚠️ Run promo plays catch-up (mutates)", emoji: "🚨" },
              // Phase 20.4 — Beat of the Week auto-select
              { path: "/admin/beat-of-the-week/auto-select", label: "🏆 Auto-pick Beat of the Week", emoji: "🏆", noConfirm: true },
            ].map((tool) => (
              <Button
                key={tool.path}
                onClick={() => runAction(tool.path, tool.label, {}, !tool.noConfirm)}
                disabled={busyAction === tool.label}
                className="bg-white/[0.04] border border-white/10 text-white hover:bg-purple-500/10 hover:border-purple-400/40 justify-start text-left text-sm py-6"
                data-testid={`cc-action-${tool.path.split("/").pop()}`}
              >
                <span className="mr-2">{tool.emoji}</span>
                {busyAction === tool.label ? "Running…" : tool.label}
              </Button>
            ))}
          </div>

          {/* Feb 2026 — Admin Artwork Control Center. Rendered as a
              navigation Link (not a Quick Fix Tool) because the
              Quick Fix Tools grid above dispatches each `path` as an
              admin-action POST via `runAction`. Artwork Control
              Center is a full admin PAGE at /admin/artwork, so it
              must use <Link>, otherwise clicking produces a 404
              from the admin-actions router. */}
          <div className="mt-3">
            <Link
              to="/admin/artwork"
              className="block w-full rounded-md border border-fuchsia-500/40 bg-fuchsia-500/5 hover:bg-fuchsia-500/15 text-white px-4 py-5 text-sm text-left"
              data-testid="cc-link-artwork-control-center"
            >
              <span className="mr-2">🎨</span>
              <span className="font-black">Artwork Control Center</span>
              <span className="block text-[11px] text-fuchsia-200/70 mt-1">
                Safe self-service artwork repair · search · inspect · refresh / replace / sync / reset · fully audited
              </span>
            </Link>
          </div>
        </Section>

        {/* iter_205 · 3b. Discovery Widgets + Beat Automation */}
        <DiscoveryAndBeatToggles />

        {/* iter_206 · 3c. Admin Coin Grants */}
        <Section id="coin-grants" title="3c. 🪙 Coin Management">
          <AdminCoinGrantsSection />
        </Section>

        {/* 4. Community Control */}
        <Section id="community" title="4. Community Control">
          {!community ? <p className="text-sm text-gray-500">Loading…</p> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <Tile label="Crews" value={community.totals?.crews} testId="cc-crews-count" />
                <Tile label="Groups" value={community.totals?.groups} testId="cc-groups-count" />
                <Tile label="Clubs" value={community.totals?.clubs} testId="cc-clubs-count" />
                <Tile label="Invite failures (7d)" value={community.invite_failures_7d} testId="cc-invite-failures" />
              </div>
              {community.broken_invite_links?.length > 0 && (
                <Card className="p-4 bg-amber-500/5 border-amber-500/30 mb-4">
                  <p className="text-amber-200 text-sm font-bold mb-2">
                    ⚠ {community.broken_invite_links.length} crew(s) missing invite_code
                  </p>
                  <div className="space-y-1.5">
                    {community.broken_invite_links.map((c) => (
                      <div
                        key={c.crew_id}
                        className="flex items-center justify-between gap-2 text-xs"
                        data-testid={`cc-broken-crew-${c.crew_id}`}
                      >
                        <span className="text-gray-200 truncate">
                          {c.name} <span className="text-gray-500">· {c.kind}</span>
                        </span>
                        <div className="flex gap-2 flex-shrink-0">
                          <Link to={`/crews/${c.crew_id}`} className="text-purple-300 underline">Open</Link>
                          <button
                            type="button"
                            onClick={() => runAction(
                              "/admin/control-center/community/regenerate-invite",
                              `Regenerate invite for ${c.name}`,
                              { crew_id: c.crew_id },
                            )}
                            className="text-amber-300 hover:text-amber-200 underline"
                            data-testid={`cc-regen-${c.crew_id}`}
                          >
                            Regenerate
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              {community.most_active?.length > 0 && (
                <Card className="p-4 bg-white/[0.03] border-white/10">
                  <p className="text-white text-sm font-bold mb-2">🔥 Most active communities</p>
                  <div className="space-y-1.5">
                    {community.most_active.map((c) => (
                      <Link
                        key={c.crew_id}
                        to={`/crews/${c.crew_id}`}
                        className="flex items-center justify-between gap-2 text-xs hover:bg-white/5 px-2 py-1.5 rounded"
                      >
                        <span className="text-gray-200 truncate">{c.name} <span className="text-gray-500">· {c.kind}</span></span>
                        <span className="text-gray-400">{c.post_count} posts · {c.member_count || 0} members</span>
                      </Link>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </Section>

        {/* 4a. RGM Official Account (bootstrap) — one-time initial
             activation of the canonical users.is_official flag on the
             pre-existing RGM Studio A&R account. READ-ONLY candidate
             preview + confirm-gated single-field write. */}
        <Section id="rgm-official" title="4a. RGM Official Account (bootstrap)">
          <Card
            className="p-4 bg-white/[0.03] border-white/10"
            data-testid="cc-rgm-official-card"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div className="min-w-0">
                <p className="text-white text-sm font-bold">
                  One-time bootstrap: promote an existing RGM Studio
                  account to <code className="text-purple-200">is_official = true</code>.
                </p>
                <p className="text-[11px] text-gray-500 mt-1">
                  Read-only preview first · Explicit confirm required
                  before any write · The write is limited to a
                  single-field <code>{'$set: { is_official: true }'}</code>.
                </p>
              </div>
              <Button
                onClick={loadOfficialCandidates}
                disabled={officialLoading}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="cc-official-load-candidates"
              >
                {officialLoading
                  ? "Loading…"
                  : (officialCandidates ? "Reload candidates" : "Load candidates")}
              </Button>
            </div>

            {officialError && (
              <p
                className="text-rose-300 text-xs mb-3"
                data-testid="cc-official-error"
              >
                {officialError}
              </p>
            )}

            {officialCandidates && (
              <>
                <div
                  className="flex items-center gap-4 flex-wrap text-[11px] text-gray-400 mb-3"
                  data-testid="cc-official-summary"
                >
                  <span>
                    current_official_user_id:{" "}
                    <code
                      className="text-white"
                      data-testid="cc-official-current-uid"
                    >
                      {officialCurrentUid || "null"}
                    </code>
                  </span>
                  <span>
                    candidates:{" "}
                    <strong className="text-white">
                      {officialCandidates.length}
                    </strong>
                  </span>
                  {officialFetchedAt && (
                    <span>fetched {officialFetchedAt.toLocaleTimeString()}</span>
                  )}
                </div>

                {officialCandidates.length === 0 ? (
                  <p
                    className="text-amber-200 text-xs"
                    data-testid="cc-official-empty"
                  >
                    No candidates match the historical signals or
                    bootstrap allowlist.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table
                      className="w-full text-xs"
                      data-testid="cc-official-candidates-table"
                    >
                      <thead>
                        <tr className="text-left text-gray-500 uppercase tracking-widest">
                          <th className="py-2 pr-3">user_id</th>
                          <th className="py-2 pr-3">name</th>
                          <th className="py-2 pr-3">artist_name</th>
                          <th className="py-2 pr-3">role</th>
                          <th className="py-2 pr-3">created_at</th>
                          <th className="py-2 pr-3">is_official</th>
                          <th className="py-2 pr-3 text-right">action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {officialCandidates.map((c) => {
                          const isBootstrap = c.user_id === RGM_BOOTSTRAP_UID;
                          const already = c.is_official === true;
                          const anotherOfficialExists =
                            !!officialCurrentUid &&
                            officialCurrentUid !== c.user_id;
                          const disableActivate =
                            !!officialActivating ||
                            already ||
                            anotherOfficialExists;
                          return (
                            <tr
                              key={c.user_id}
                              className={`border-t border-white/5 ${
                                isBootstrap ? "bg-purple-500/10" : ""
                              }`}
                              data-testid={`cc-official-row-${c.user_id}`}
                            >
                              <td className="py-2 pr-3 text-white font-mono break-all">
                                {isBootstrap && (
                                  <span
                                    className="mr-1 text-purple-300"
                                    aria-label="bootstrap-target"
                                    data-testid="cc-official-bootstrap-marker"
                                  >
                                    ★
                                  </span>
                                )}
                                {c.user_id}
                              </td>
                              <td className="py-2 pr-3 text-gray-200">
                                {c.name || "—"}
                              </td>
                              <td
                                className={`py-2 pr-3 ${
                                  isBootstrap
                                    ? "text-purple-100 font-bold"
                                    : "text-gray-200"
                                }`}
                              >
                                {c.artist_name || "—"}
                              </td>
                              <td className="py-2 pr-3 text-gray-300">
                                {c.role || "—"}
                              </td>
                              <td className="py-2 pr-3 text-gray-400">
                                {c.created_at
                                  ? new Date(c.created_at).toLocaleString()
                                  : "—"}
                              </td>
                              <td className="py-2 pr-3">
                                {already ? (
                                  <span className="text-emerald-300 font-bold">
                                    true
                                  </span>
                                ) : (
                                  <span className="text-gray-400">false</span>
                                )}
                              </td>
                              <td className="py-2 pr-3 text-right">
                                <Button
                                  onClick={() =>
                                    activateOfficial(c.user_id, c.artist_name)
                                  }
                                  disabled={disableActivate}
                                  className="bg-rose-600 hover:bg-rose-500 text-white text-xs px-3 py-1"
                                  data-testid={`cc-official-activate-${c.user_id}`}
                                  title={
                                    already
                                      ? "Already official"
                                      : anotherOfficialExists
                                        ? "Another account is already official"
                                        : "Open confirm dialog before activating"
                                  }
                                >
                                  {officialActivating === c.user_id
                                    ? "Activating…"
                                    : "Activate"}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </Card>
        </Section>

        {/* 5. Reports Control */}
        <Section
          id="reports"
          title="5. Reports Control"
          action={
            <div className="flex gap-2">
              <Link to="/admin/reports">
                <Button className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="cc-open-reports">
                  Open Reports admin
                </Button>
              </Link>
            </div>
          }
        >
          {!reportsOverview ? <p className="text-sm text-gray-500">Loading…</p> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                <Tile label="Total" value={reportsOverview.totals?.total} testId="cc-reports-total" />
                <Tile label="Published" value={reportsOverview.totals?.published} />
                <Tile label="Drafts" value={reportsOverview.totals?.drafts} />
                <Tile label="Featured" value={reportsOverview.totals?.featured} />
                <Tile label="Missing cover" value={reportsOverview.totals?.missing_cover} testId="cc-reports-missing-cover" />
              </div>
              {reportsOverview.recent?.length > 0 && (
                <Card className="p-4 bg-white/[0.03] border-white/10">
                  <p className="text-white text-sm font-bold mb-2">Recent reports</p>
                  <div className="space-y-1.5">
                    {reportsOverview.recent.map((r) => (
                      <Link
                        key={r.report_id}
                        to={`/reports/${r.report_id}`}
                        className="flex items-center justify-between gap-2 text-xs hover:bg-white/5 px-2 py-1.5 rounded"
                      >
                        <span className="text-gray-200 truncate">{r.title || "Untitled"}</span>
                        <span className={r.published ? "text-emerald-300" : "text-amber-300"}>
                          {r.published ? "published" : "draft"}
                        </span>
                      </Link>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </Section>

        {/* 6. Beat Department Control */}
        <Section id="beats" title="6. Beat Department Control" action={
          <Link to="/admin/beats">
            <Button variant="outline" className="text-white border-gray-700" data-testid="cc-open-beats">Open Beats admin</Button>
          </Link>
        }>
          {!beats ? <p className="text-sm text-gray-500">Loading…</p> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                <Tile label="Total beats" value={beats.totals?.total} testId="cc-beats-total" />
                <Tile label="Active producers" value={beats.totals?.active_producers} />
                <Tile label="Featured" value={beats.totals?.featured} />
                <Tile label="Sales (paid)" value={beats.totals?.sales} />
                <Tile label="Gifts received" value={beats.totals?.gifts} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                <Tile label="Plays" value={beats.engagement?.plays?.toLocaleString?.() || beats.engagement?.plays} />
                <Tile label="Downloads" value={beats.engagement?.downloads} />
                <Tile label="Likes" value={beats.engagement?.likes} />
                <Tile label="Comments" value={beats.engagement?.comments} />
                <Tile label="Reports" value={beats.errors_count} />
              </div>
              <Card className="p-4 bg-amber-500/5 border-amber-500/30">
                <p className="text-amber-200 text-xs font-bold mb-2">
                  💡 Beat Department is kept SEPARATE from Artist promotion lanes.
                </p>
                {beats.beat_of_the_week && (
                  <p className="text-gray-300 text-sm">
                    🎖️ Beat of the Week: <Link className="text-amber-300 underline" to={`/beats/${beats.beat_of_the_week.beat_id}`}>{beats.beat_of_the_week.title}</Link>
                  </p>
                )}
                {beats.trending?.length > 0 && (
                  <p className="text-gray-300 text-sm mt-2">
                    🔥 Trending: {beats.trending.map((b, i) => (
                      <Link key={b.beat_id} to={`/beats/${b.beat_id}`} className="text-purple-300 underline ml-1">
                        {b.title}{i < beats.trending.length - 1 ? "," : ""}
                      </Link>
                    ))}
                  </p>
                )}
              </Card>
            </>
          )}
        </Section>

        {/* 7. Payout / Economy */}
        <Section id="payouts" title="7. Payout / Economy Control" action={
          <Link to="/admin/payouts">
            <Button className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="cc-open-payouts">Open Admin Payouts</Button>
          </Link>
        }>
          {!payouts ? <p className="text-sm text-gray-500">Loading…</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Tile label="Pending payout requests" value={payouts.pending_payout_requests} testId="cc-payouts-pending" />
              <Tile label="Creator liability"
                    value={payouts.creator_liability_cents != null ? `$${(payouts.creator_liability_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"} />
              <Tile label="Eligible total"
                    value={payouts.eligible_payout_total_cents != null ? `$${(payouts.eligible_payout_total_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"} />
            </div>
          )}
        </Section>

        {/* 8. Growth */}
        <Section id="growth" title="8. Growth Dashboard">
          {!growth ? <p className="text-sm text-gray-500">Loading…</p> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <Tile label="Total users" value={growth.total_users?.toLocaleString?.()} testId="cc-growth-users" />
              <Tile label="Artists" value={growth.artists} />
              <Tile label="Producers" value={growth.producers} />
              <Tile label="Supporters" value={growth.supporters} />
              <Tile label="Active (7d)" value={growth.active_users_7d} />
              <Tile label="New signups (7d)" value={growth.new_signups_7d} />
              <Tile label="New signups (30d)" value={growth.new_signups_30d} />
              <Tile label="Referrals" value={growth.referrals} />
              <Tile label="Gifts sent" value={growth.gifts_sent} />
              <Tile label="Beats uploaded" value={growth.beats_uploaded} />
              <Tile label="Tracks uploaded" value={growth.tracks_uploaded} />
            </div>
          )}
        </Section>

        {/* 9. Homepage Controls — Batch 2 (Weekly Timer, Live Promo, Announcement, Boost Pulse) */}
        <Section id="homepage-controls" title="9. Homepage Controls">
          <HomepageControlsSection />
        </Section>

        {/* 10. Contest Center — Batch 3 shortcuts */}
        <Section id="contest-center" title="10. Contest Center">
          {/* Prominent ON/OFF toggle so admins never need to hunt for the flag. */}
          <div className="mb-3">
            <ContestFlagToggle />
          </div>
          {/* Phase 1 Contest Judging master switch — mirrors the Contest
              Center toggle. Default fail-closed OFF. Turning ON only
              makes the already-built /judge routes available; it does
              NOT change any contest's status. */}
          <div className="mb-3" data-testid="admin-cc-contest-judging-toggle">
            <ContestJudgingFlagToggle />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="admin-cc-contest-tiles">
            <Link
              to="/admin/contests"
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.07] transition-colors"
              data-testid="admin-cc-open-contests"
            >
              <p className="text-lg font-black text-amber-200">🏆 Contests</p>
              <p className="text-xs text-gray-400">Create · publish · judge · award</p>
            </Link>
            <Link
              to="/admin/badge-catalog"
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.07] transition-colors"
              data-testid="admin-cc-open-badge-catalog"
            >
              <p className="text-lg font-black text-purple-200">🏅 Badge Catalog</p>
              <p className="text-xs text-gray-400">Legacy + custom + per-contest badges</p>
            </Link>
            <Link
              to="/admin/hall-of-champions"
              className="rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:bg-white/[0.07] transition-colors"
              data-testid="admin-cc-open-hof"
            >
              <p className="text-lg font-black text-cyan-200">🏛 Hall of Champions</p>
              <p className="text-xs text-gray-400">Read-only winner archive</p>
            </Link>
          </div>
          <p className="mt-3 text-[11px] text-gray-500" data-testid="admin-cc-contest-flag-note">
            Contests are feature-flag gated. The public /contests page + entries only work when the flag is ON.
          </p>
        </Section>

        {/* iter_184 — 🛠 Maintenance & Backfill (permanent) */}
        <Section id="maintenance" title="11. 🛠 Maintenance & Backfill">
          <AdminContestBackfillSection />
        </Section>
      </div>

      <ConfirmDialog
        open={!!confirmFor}
        title={confirmFor?.label}
        body={confirmFor?.body}
        onCancel={() => setConfirmFor(null)}
        onConfirm={() => confirmFor?.action()}
        confirmLabel="Run"
      />
    </div>
  );
};

export default AdminControlCenterPage;
